import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloudIcon from '@mui/icons-material/Cloud';
import CodeIcon from '@mui/icons-material/Code';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import BrushIcon from '@mui/icons-material/Brush';
import GroupIcon from '@mui/icons-material/Group';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
// Smaller logo for header (300w) to reduce LCP; displayed at 70px
import logoImage from '../../assets/logo-300w.webp';
import CapabilitiesMegaMenu from './CapabilitiesMegaMenu';
import ServicesMegaMenu from './ServicesMegaMenu';

const Navbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [servicesMegaMenuOpen, setServicesMegaMenuOpen] = useState(false);
  const [mobileCapabilitiesOpen, setMobileCapabilitiesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1040));
  const isDesktop = useMediaQuery(theme.breakpoints.up(1040));
  const [atTop, setAtTop] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setAtTop(window.scrollY < 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMegaMenuToggle = () => {
    setMegaMenuOpen(!megaMenuOpen);
  };

  const handleMegaMenuClose = () => {
    setMegaMenuOpen(false);
  };

  const handleServicesMegaMenuToggle = () => {
    setServicesMegaMenuOpen(!servicesMegaMenuOpen);
    setMegaMenuOpen(false);
  };
  const handleServicesMegaMenuClose = () => {
    setServicesMegaMenuOpen(false);
  };

  const handleMobileCapabilitiesToggle = () => {
    setMobileCapabilitiesOpen(!mobileCapabilitiesOpen);
    setMobileServicesOpen(false);
  };

  const handleMobileServicesToggle = () => {
    setMobileServicesOpen(!mobileServicesOpen);
    setMobileCapabilitiesOpen(false);
  };

  const menuItems = [
    { text: 'Portfolio', path: '/portfolio' },
    { text: 'About Us', path: '/about' },
    { text: 'Blogs', path: '/blogs' },
    { text: 'Hiring', path: '/hiring', highlight: true },
  ];

  const mobileCapabilities = [
    {
      title: 'Artificial Intelligence',
      description: 'Gen AI, Custom LLMs, Machine Learning & Computer Vision',
      icon: <SmartToyIcon sx={{ fontSize: 24, color: '#6366F1' }} />,
      path: '/capabilities/ai',
    },
    {
      title: 'AI Sales Agents',
      description: 'Intelligent automation, Lead generation & Customer engagement',
      icon: <SupportAgentIcon sx={{ fontSize: 24, color: '#EC4899' }} />,
      path: '/capabilities/ai-sales',
    },
    {
      title: 'Cloud Solutions',
      description: 'Infrastructure, DevOps, APIs, Automation & Scalability',
      icon: <CloudIcon sx={{ fontSize: 24, color: '#F59E42' }} />,
      path: '/capabilities/cloud-solution',
    },
    {
      title: 'Web Development',
      description: 'React, Node.js, Ruby on Rails & Full-stack Solutions',
      icon: <CodeIcon sx={{ fontSize: 24, color: '#10B981' }} />,
      path: '/capabilities/web-development',
    },
    {
      title: 'Mobile Development',
      description: 'Native iOS & Android, React Native & Cross-platform',
      icon: <PhoneAndroidIcon sx={{ fontSize: 24, color: '#3B82F6' }} />,
      path: '/capabilities/mobile-development',
    },
    {
      title: 'UX/UI Design',
      description: 'User Research, Wireframing, Prototyping & Design Systems',
      icon: <BrushIcon sx={{ fontSize: 24, color: '#FBBF24' }} />,
      path: '/capabilities/ui-ux',
    },
  ];

  const mobileServices = [
    {
      title: 'Staff Augmentation',
      description: 'Instantly scale your team with top tech talent.',
      icon: <GroupIcon sx={{ fontSize: 24, color: '#6366F1' }} />,
      path: '/services/staff-augmentation',
    },
    {
      title: 'Product Studio',
      description: 'From idea to launch, we build your product.',
      icon: <DesignServicesIcon sx={{ fontSize: 24, color: '#EC4899' }} />,
      path: '/services/product-studio',
    },
    {
      title: 'Project Sprints',
      description: 'Agile sprints for rapid, high-quality delivery.',
      icon: <FlashOnIcon sx={{ fontSize: 24, color: '#F59E42' }} />,
      path: '/services/project-sprints',
    },
    {
      title: 'IT Outsourcing',
      description: 'Expert IT teams & consulting for US businesses.',
      icon: <BusinessCenterIcon sx={{ fontSize: 24, color: '#6366F1' }} />,
      path: '/services/it-outsourcing',
    },
    {
      title: 'Custom Software Dev',
      description: 'Bespoke software built for your exact needs.',
      icon: <CodeIcon sx={{ fontSize: 24, color: '#EC4899' }} />,
      path: '/services/custom-software-development',
    },
    {
      title: 'GoHighLevel Expert',
      description: 'GHL automation, funnels & white-label SaaS.',
      icon: <AutoFixHighIcon sx={{ fontSize: 24, color: '#F59E42' }} />,
      path: '/services/gohighlevel-expert',
    },
  ];

  const drawer = (
    <List className="bg-black h-full" sx={{ paddingTop: { xs: '6rem', sm: '6rem' } }}>
      <ListItem
        onClick={handleDrawerToggle}
        component={Link}
        to="/"
        sx={{ 
          '&:hover': { backgroundColor: 'rgba(246, 255, 122, 0.1)' },
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <ListItemText 
          primary="Home" 
          className="text-white" 
          primaryTypographyProps={{ 
            style: { 
              color: '#ffffff',
              fontWeight: 400
            } 
          }}
        />
      </ListItem>
      
      {/* Capabilities with Expandable Mega Menu */}
      <ListItem
        onClick={handleMobileCapabilitiesToggle}
        sx={{ 
          '&:hover': { backgroundColor: 'rgba(246, 255, 122, 0.1)' },
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          cursor: 'pointer'
        }}
      >
        <ListItemText 
          primary="Capabilities" 
          className="text-white" 
          primaryTypographyProps={{ 
            style: { 
              color: '#ffffff',
              fontWeight: 400
            } 
          }}
        />
        {mobileCapabilitiesOpen ? <ExpandLessIcon sx={{ color: '#ffffff' }} /> : <ExpandMoreIcon sx={{ color: '#ffffff' }} />}
      </ListItem>
      
      <Collapse in={mobileCapabilitiesOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
          {mobileCapabilities.map((capability) => (
            <ListItem
              key={capability.title}
              onClick={handleDrawerToggle}
              component={Link}
              to={capability.path}
              sx={{ 
                pl: 4,
                '&:hover': { backgroundColor: 'rgba(246, 255, 122, 0.1)' },
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ mr: 2 }}>
                  {capability.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mb: 0.5 }}>
                    {capability.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem' }}>
                    {capability.description}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </Collapse>

      {/* Services with Expandable Mega Menu */}
      <ListItem
        onClick={handleMobileServicesToggle}
        sx={{ 
          '&:hover': { backgroundColor: 'rgba(246, 255, 122, 0.1)' },
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          cursor: 'pointer'
        }}
      >
        <ListItemText 
          primary="Services" 
          className="text-white" 
          primaryTypographyProps={{ 
            style: { 
              color: '#ffffff',
              fontWeight: 400
            } 
          }}
        />
        {mobileServicesOpen ? <ExpandLessIcon sx={{ color: '#ffffff' }} /> : <ExpandMoreIcon sx={{ color: '#ffffff' }} />}
      </ListItem>
      
      <Collapse in={mobileServicesOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
          {mobileServices.map((service) => (
            <ListItem
              key={service.title}
              onClick={handleDrawerToggle}
              component={Link}
              to={service.path}
              sx={{ 
                pl: 4,
                '&:hover': { backgroundColor: 'rgba(246, 255, 122, 0.1)' },
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ mr: 2 }}>
                  {service.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mb: 0.5 }}>
                    {service.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem' }}>
                    {service.description}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </Collapse>

      {menuItems.map((item) => (
        <ListItem
          key={item.text}
          onClick={handleDrawerToggle}
          component={Link}
          to={item.path}
          sx={{ 
            '&:hover': { backgroundColor: 'rgba(246, 255, 122, 0.1)' },
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <ListItemText 
            primary={item.text} 
            className={item.highlight ? "font-medium" : "text-white"} 
            primaryTypographyProps={{ 
              style: { 
                color: item.highlight ? '#f6ff7a' : '#ffffff',
                fontWeight: item.highlight ? 500 : 400
              } 
            }}
          />
        </ListItem>
      ))}
      <ListItem sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginTop: { xs: '2rem', sm: '1.5rem' },
        paddingBottom: { xs: '2rem', sm: '1.5rem' }
      }}>
        <Button
          component={Link}
          to="/contact"
          variant="contained"
          className="text-black rounded-full px-4 py-2 font-medium"
          endIcon={<ArrowForwardIcon />}
          sx={{ 
            backgroundColor: '#f6ff7a', 
            '&:hover': { 
              backgroundColor: '#e6ef6c' 
            },
            color: '#000000'
          }}
        >
          Get In Touch
        </Button>
      </ListItem>
    </List>
  );

  return (
    <>
      <Box
        sx={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 1300,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
                <AppBar
          position="static"
          elevation={atTop && !mobileOpen ? 6 : 0}
          sx={{
            width: atTop && !mobileOpen ? { xs: '100%', sm: '100%', md: '95%', lg: '90%' } : '100%',
            mt: atTop && !mobileOpen ? { xs: 0, sm: 0, md: 4, lg: 4 } : 0,
            backgroundColor: '#000',
            color: '#fff',
            borderRadius: atTop && !mobileOpen ? { xs: 0, sm: 0, md: 8, lg: 8 } : 0,
            boxShadow: atTop && !mobileOpen ? { xs: '0 4px 20px 0 rgba(0,0,0,0.15)', sm: '0 4px 20px 0 rgba(0,0,0,0.15)', md: '0 8px 32px 0 rgba(31,41,55,0.10)', lg: '0 8px 32px 0 rgba(31,41,55,0.10)' } : 'none',
            transition: atTop && !mobileOpen
              ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
              : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'width, margin, border-radius, box-shadow, transform',
            pointerEvents: 'auto',
            ...(atTop && !mobileOpen && !megaMenuOpen && !servicesMegaMenuOpen && {
              transform: { xs: 'scale(1)', sm: 'scale(1)', md: 'scale(1)', lg: 'scale(1)' },
              '&:hover': {
                transform: { xs: 'scale(1)', sm: 'scale(1)', md: 'scale(1.015)', lg: 'scale(1.015)' },
                boxShadow: { xs: '0 4px 20px 0 rgba(0,0,0,0.15)', sm: '0 4px 20px 0 rgba(0,0,0,0.15)', md: '0 12px 36px 0 rgba(31,41,55,0.16)', lg: '0 12px 36px 0 rgba(31,41,55,0.16)' },
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              },
            }),
          }}
        >
          <Toolbar className="container mx-auto px-4" sx={{ minHeight: '100px', py: 2 }}>
          <Link to="/" className="flex-grow">
            <Box className="flex items-center">
              <img 
                src={logoImage} 
                alt="BTI Logo" 
                width={140}
                height={140}
                decoding="async"
                className="h-[70px] mr-2 w-auto"
                style={{ height: '70px', maxWidth: 140, objectFit: 'contain' }}
              />
              <div className="flex flex-col">
                <span className="text-white text-xl font-semibold tracking-widest italic" style={{ letterSpacing: '0.15em' }}>Brown Tech Int.</span>
                <span className="text-gray-400 text-sm">Your Business & Our Passion</span>
              </div>
            </Box>
          </Link>
          
          {/* Desktop menu */}
          {isDesktop && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3 
          }}>
              <ClickAwayListener onClickAway={handleMegaMenuClose}>
                <Box sx={{ position: 'relative' }}>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMegaMenuToggle();
                      setServicesMegaMenuOpen(false);
                    }}
                    className="hover:text-highlight text-base font-medium transition-colors duration-200"
                    style={{ color: megaMenuOpen ? '#f6ff7a' : '#ffffff' }}
                  >
                    Capabilities
                  </Link>
                  {megaMenuOpen && <CapabilitiesMegaMenu onClose={handleMegaMenuClose} />}
                </Box>
              </ClickAwayListener>
              <ClickAwayListener onClickAway={handleServicesMegaMenuClose}>
                <Box sx={{ position: 'relative' }}>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleServicesMegaMenuToggle();
                      setMegaMenuOpen(false);
                    }}
                    className="hover:text-highlight text-base font-medium transition-colors duration-200"
                    style={{ color: servicesMegaMenuOpen ? '#f6ff7a' : '#ffffff' }}
                  >
                    Services
                  </Link>
                  {servicesMegaMenuOpen && <ServicesMegaMenu onClose={handleServicesMegaMenuClose} />}
                </Box>
              </ClickAwayListener>
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                  className="hover:text-highlight text-base font-medium transition-colors duration-200"
                style={{ 
                  color: item.highlight ? '#f6ff7a' : '#ffffff'
                }}
              >
                {item.text}
              </Link>
            ))}
            <Button
              component={Link}
              to="/contact"
              variant="contained"
                className="rounded-full px-6 py-3 ml-2 text-lg font-medium transition-all duration-200"
              endIcon={<ArrowForwardIcon />}
              sx={{ 
                backgroundColor: '#f6ff7a', 
                color: '#000000',
                '&:hover': { 
                  backgroundColor: '#e6ef6c' 
                },
                borderRadius: '9999px',
                textTransform: 'none'
              }}
            >
              Get In Touch
            </Button>
          </Box>
          )}

          {/* Mobile menu button - only visible on mobile */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label={mobileOpen ? "close drawer" : "open drawer"}
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: '#ffffff' }}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      </Box>
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '320px' },
            backgroundColor: '#000000'
          }
        }}
      >
        {drawer}
      </Drawer>
      <div className="h-24"></div> {/* Spacer to prevent content from being hidden under the fixed navbar */}
    </>
  );
};

export default Navbar; 